import { listen, qs, qsa } from "@fluorescent/dom";
import { slideDown, slideUp, slideStop } from "slide-anim";

import {
  updateFilters,
  rangeRemoved,
  filterChanged,
} from "@/lib/filters/events";
import priceRange from "@/lib/filters/price-range";
import FilterSearch from "@/lib/filters/filter-search";
import updateInnerHTML from "@/lib/update-inner-html";
import { on } from "@/glow/events";
import debounce from "@/utils/debounce";

const selectors = {
  scopedForm: formType => `.filter-form[data-form-type="${formType}"]`,
  filterForm: "[data-filter-form]",
  filterItem: "[data-filter-item]",
  filterList: ".filter-form__list-wrapper",
  groupContents: ".filter-form__group-filter-wrapper",
  filterItemInput: "[data-filter-item-input]",
  group: "[data-filter-group]",
  groupToggle: "[data-filter-group-toggle]",
  clearGroup: "[data-filter-group-clear]",
  priceRange: "[data-price-range]",
  rangeInput: "[data-range-input]",
  rangeInputMin: '[data-range-input-type="min"]',
  rangeInputMax: '[data-range-input-type="max"]',
  clearAll: "[data-clear-all-filters]",
  filterTruncateToggle: "[data-filter-truncate-toggle]",
  filterTruncateToggleText: "[data-truncate-toggle-text]",
  shouldTruncate: '[data-filter-item-should-truncate="true"]',
};

/**
 * FilterForm
 * @param {Object} node the filter form
 * @returns {Function} unload
 * @returns {Function} renderFilters
 */
const FilterForm = node => {
  if (!node) return false;

  const { formType } = node.dataset;
  const groupEls = qsa(selectors.group, node);
  const groupToggleEls = qsa(selectors.groupToggle, node);
  const clearGroupEls = qsa(selectors.clearGroup, node);
  const clearAllEls = qsa(selectors.clearAll, node);
  const rangeInputs = qsa(selectors.rangeInput, node);
  const rangeContainer = qs(selectors.priceRange, node);

  const filterSearch = FilterSearch(node);
  let range = null;

  if (rangeContainer) {
    range = priceRange(rangeContainer);
    rangeRemoved(handleRangeRemoved);
  }

  const events = [
    listen(node, "keydown", handleSubmit),
    listen(node, "change", changeHandler),
    listen(groupToggleEls, "click", toggleGroup),
    listen(rangeInputs, "change", rangeChanged),
    listen(clearGroupEls, "click", clearGroup),
    listen(clearAllEls, "click", clearAllFilters),
    listen(groupEls, "click", toggleGroupTruncate),
    on("filters:filter-removed", () => syncActiveStates()),
  ];

  function handleSubmit(evt) {
    // Pressing "Enter" on a price range input submits the form, but also causes
    // a "change" event, which leads to a double load. Instead, we prevent "Enter"
    // from applying, and blur the input to trigger the "change".
    if (evt.code == "Enter" && evt.target.hasAttribute("data-range-input")) {
      evt.preventDefault();
      evt.target.blur();
    }
  }

  function changeHandler(evt) {
    if (evt.target.classList.contains("filter-item__checkbox")) {
      filterChange(evt.target);
    } else if (evt.target.classList.contains("filter-item__radio")) {
      sortChange(evt);
    }
  }

  function filterChange(filter) {
    if (filter.dataset.filterItemContentDisabled === "true") {
      return;
    }

    checkForActiveFilters(filter);

    range && range.validateRange();

    // Emit a filter changed event to apply loading styles
    filterChanged();

    let shouldDebounce = true;

    if (filter.id.includes("filter.v.availability")) {
      // HACK: avoid debounce on the in-stock toggle.  Later we should see about
      // a better way to decide what changes get debounced vs not, it is def
      // necessary to debounce on multi select like color or size
      shouldDebounce = false;
    }

    // Debounce the actual filter update event to allow for multiple filter changes in quick succsession
    if (shouldDebounce) {
      debounce(() => updateFilters(node), 1000)();
    } else {
      updateFilters(node);
    }
  }

  function sortChange(e) {
    checkForActiveFilters(e.target);
    range && range.validateRange();
    updateFilters(node);
  }

  function rangeChanged(e) {
    const input = e.currentTarget;

    updateRangeInputs(input);
    checkForActiveFilters(input);
    range && range.validateRange();
    updateFilters(node);
  }

  function updateRangeInputs(input) {
    const type = input.getAttribute("data-range-input-type");

    const oppositeBoundSelectorMap = {
      "min": "rangeInputMax",
      "max": "rangeInputMin",
    };

    const oppositeBoundAttributeMap = {
      "min": "max",
      "max": "min",
    };

    const priceRange = input.closest(selectors.priceRange);

    const oppositeBound = qs(
      selectors[oppositeBoundSelectorMap[type]],
      priceRange
    );

    if (input.value === input.getAttribute(type)) {
      if (
        oppositeBound.value ===
        oppositeBound.getAttribute(oppositeBoundAttributeMap[type])
      ) {
        input.value = "";
        oppositeBound.value = "";
      }
    }
  }

  function handleRangeRemoved() {
    range?.reset();
  }

  function toggleGroup(evt) {
    evt.preventDefault();
    const group = qs(`#${evt.currentTarget.getAttribute("aria-controls")}`);
    const ariaExpanded =
      evt.currentTarget.getAttribute("aria-expanded") === "true";

    slideStop(group);

    if (ariaExpanded) {
      closeGroup(evt.currentTarget, group);
    } else {
      openGroup(evt.currentTarget, group);
    }
  }

  function openGroup(button, group) {
    if (formType === "drawer") {
      qsa(selectors.groupToggle, group).forEach(toggle => {
        toggle.setAttribute("aria-expanded", true);
      });
    } else {
      slideDown(group);
      button.setAttribute("aria-expanded", true);
    }

    group.setAttribute("aria-hidden", false);
  }

  function closeGroup(button, group) {
    if (formType === "drawer") {
      qsa(selectors.groupToggle, group).forEach(toggle => {
        toggle.setAttribute("aria-expanded", true);
      });
    } else {
      slideUp(group);
      button.setAttribute("aria-expanded", false);
    }

    group.setAttribute("aria-hidden", true);
  }

  function clearGroup(evt) {
    evt.preventDefault();
    const group = evt.currentTarget.closest(selectors.group);

    if (group) {
      qsa(selectors.filterItemInput, group).forEach(input => {
        if (input.type === "checkbox" || input.type === "radio") {
          input.checked = false;
        } else {
          input.value = "";
          range?.reset();
        }
      });

      updateFilters(node);
      checkGroupActiveFilters(group);
    }
  }

  function clearAllFilters(emitUpdate = true) {
    range?.reset();

    qsa(`${selectors.filterItemInput}`, node).forEach(input => {
      if (input.type === "checkbox" || input.type === "radio") {
        input.checked = false;
      }
    });

    if (emitUpdate) {
      updateFilters(node);
    }

    groupEls.forEach(checkGroupActiveFilters);
  }

  function toggleGroupTruncate(evt) {
    const truncateToggleEl = evt.target.closest(selectors.filterTruncateToggle);
    const groupEl = evt.target.closest(selectors.group);

    if (truncateToggleEl && groupEl) {
      const shouldTruncateEls = qsa(selectors.shouldTruncate, groupEl);

      shouldTruncateEls.forEach(element => {
        element.dataset.filterItemIsTruncated =
          element.dataset.filterItemIsTruncated === "true" ? "false" : "true";
      });

      truncateToggleEl.setAttribute(
        "aria-expanded",
        truncateToggleEl.getAttribute("aria-expanded") === "true"
          ? "false"
          : "true"
      );

      const truncateToggleTextEl = qs(
        selectors.filterTruncateToggleText,
        truncateToggleEl
      );

      const buttonLabelAttr =
        truncateToggleEl.getAttribute("aria-expanded") === "true"
          ? "data-show-more-text"
          : "data-show-less-text";

      truncateToggleTextEl.innerHTML =
        truncateToggleEl.getAttribute(buttonLabelAttr);
    }
  }

  function containsCheckedInputs(items) {
    return items.some(input => input.checked);
  }

  function rangeInputsHaveValue() {
    return rangeInputs.some(input => {
      let inputHasValue = input.value !== "";

      if (inputHasValue) {
        const type = input.getAttribute("data-range-inut-type");

        inputHasValue = input.value !== input.getAttribute(type);
      }

      return inputHasValue;
    });
  }

  function checkGroupActiveFilters(group) {
    if (group) {
      const { filterType } = group.dataset;

      if (filterType === "list" || filterType === "boolean") {
        group.dataset.filterGroupHasActiveFilters = containsCheckedInputs(
          qsa("input", group)
        );
      } else if (filterType === "price_range") {
        group.dataset.filterGroupHasActiveFilters = rangeInputsHaveValue();
      }
    }
  }

  function checkForActiveFilters(filterItem) {
    const group = filterItem.closest(selectors.group);
    const activeItems =
      containsCheckedInputs(qsa("input", node)) || rangeInputsHaveValue();

    checkGroupActiveFilters(group);
    node.dataset.filtersActive = activeItems;
  }

  function syncActiveStates() {
    let activeItems = false;
    const rangeInputs = qs("[data-range-input]", node);

    if (containsCheckedInputs(qsa("input", node))) {
      activeItems = true;
    }

    if (rangeInputs && rangeInputsHaveValue()) {
      activeItems = true;
    } else {
      range?.reset();
    }

    node.dataset.filtersActive = activeItems;
    groupEls.forEach(checkGroupActiveFilters);
  }

  function renderFilters(doc) {
    // This updates the contents of the filter groups, we omit the price
    // group because it doesn't change in liquid and there is a JS slider
    const updatedGroupContents = qsa(
      `${selectors.scopedForm(formType)} ${
        selectors.groupContents
      }:not([data-filter-type="price_range"])`,
      doc
    );

    updatedGroupContents.forEach(element => {
      const currentGroupSelector = `${selectors.scopedForm(formType)} ${
        selectors.groupContents
      }#${element.id}`;

      // In the drawer, the full HTML update wipes the event listeners, so we need to
      // scope the replacement to the actual filter content, and then update the "Reset"
      // button attribute for active filters
      if (formType === "drawer") {
        const oldGroupElement = qs(currentGroupSelector, node);
        const newGroupElement = qs(currentGroupSelector, doc);

        const oldClearGroupEl = qs(selectors.clearGroup, oldGroupElement);
        const newClearGroupEl = qs(selectors.clearGroup, newGroupElement);

        oldClearGroupEl.dataset.hasActiveFilters =
          newClearGroupEl.dataset.hasActiveFilters;
        updateInnerHTML(
          `${selectors.scopedForm(formType)} ${selectors.groupContents}#${
            element.id
          } ${selectors.filterList}`,
          doc
        );
      } else {
        updateInnerHTML(currentGroupSelector, doc);
      }
    });

    // This updates the counts and labels, without changing the toggled state
    if (formType !== "drawer") {
      const updatedGroupToggles = qsa(
        `${selectors.scopedForm(formType)} ${selectors.groupToggle}`,
        doc
      );

      updatedGroupToggles.forEach(element => {
        const groupToggleValue = element.getAttribute(
          "data-filter-group-toggle"
        );

        updateInnerHTML(
          `${selectors.scopedForm(
            formType
          )} [data-filter-group-toggle="${groupToggleValue}"]`,
          doc
        );
      });

      const updatedClearGroups = qsa(
        `${selectors.scopedForm(formType)} ${selectors.clearGroup}`,
        doc
      );

      updatedClearGroups.forEach(element => {
        const clearGroupValue = element.getAttribute("data-filter-group-clear");

        const oldClearButton = qs(
          `${selectors.scopedForm(
            formType
          )} [data-filter-group-clear="${clearGroupValue}"]`,
          node
        );

        const newClearButton = qs(
          `${selectors.scopedForm(
            formType
          )} [data-filter-group-clear="${clearGroupValue}"]`,
          doc
        );

        if (oldClearButton && newClearButton) {
          oldClearButton.setAttribute(
            "data-has-active-filters",
            newClearButton.getAttribute("data-has-active-filters")
          );
        }
      });
    }
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    range && range.unload();
    filterSearch && filterSearch.unload();
  }

  return {
    unload,
    renderFilters,
    clearAllFilters,
  };
};

export default FilterForm;
